import { useEffect } from "react";
import { Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuthContext } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { DateRangeProvider } from "@/state/DateRangeContext";
import { EntitlementsProvider } from "@/contexts/EntitlementsContext";
import {
  DashboardPageAccessProvider,
  useDashboardPageAccess,
} from "@/contexts/DashboardPageAccessContext";
import { AgentPresenceProvider } from "@/contexts/AgentPresenceContext";
import { useInboxMessageAlerts } from "@/hooks/useInboxMessageAlerts";
import { pageKeyForPathname } from "@/lib/dashboardPages";
import { readStoredSession, hasValidLoginGrants } from "@/lib/authSession";

/**
 * Wraps all routes that require a session. Sends guests to /login with return path.
 * Also waits for workspace context to settle before rendering child routes so that
 * page components never fire BFF calls with a null X-Workspace-Id header.
 */
export default function ProtectedLayout() {
  const { isAuthenticated, isInitializing, signOut } = useAuthContext();
  const { loading: workspaceLoading, workspaces, error: workspaceError } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isInitializing) return;
    if (!isAuthenticated) {
      navigate("/login", { replace: true, state: { from: location } });
      return;
    }
    if (!hasValidLoginGrants(readStoredSession())) {
      void signOut();
    }
  }, [isAuthenticated, isInitializing, navigate, location, signOut]);

  if (isInitializing || workspaceLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-app-canvas text-sm font-medium text-slate-500 dark:text-slate-400"
        role="status"
      >
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!workspaceLoading && workspaces.length === 0) {
    if (workspaceError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-app-canvas px-6 text-center">
          <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
            Unable to load workspaces
          </p>
          <p className="max-w-sm text-sm text-slate-500 dark:text-slate-400">
            {workspaceError?.message ||
              "There was a problem connecting to the server. Please check your network and try again."}
          </p>
          <button
            className="ui-btn-primary mt-2"
            onClick={() => window.location.reload()}
            type="button"
          >
            Retry
          </button>
          <button
            className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
            onClick={() => void signOut()}
            type="button"
          >
            Sign out
          </button>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-app-canvas px-6 text-center">
        <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
          No workspace configured
        </p>
        <p className="max-w-sm text-sm text-slate-500 dark:text-slate-400">
          Your account does not have a workspace assigned yet. Contact your administrator to set one
          up, or try signing in again once the workspace has been provisioned.
        </p>
        <button
          className="ui-btn-primary mt-2"
          onClick={() => void signOut()}
          type="button"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <DashboardPageAccessProvider>
      <EntitlementsProvider>
        <AgentPresenceProvider>
          <ProtectedLayoutOutlet />
        </AgentPresenceProvider>
      </EntitlementsProvider>
    </DashboardPageAccessProvider>
  );
}

function ProtectedLayoutOutlet() {
  const { activeWorkspaceId } = useWorkspace();
  const { canAccessPage, loading: pageAccessLoading } = useDashboardPageAccess();
  const location = useLocation();
  const pageKey = pageKeyForPathname(location.pathname);
  const blockedByRbac = Boolean(pageKey && !pageAccessLoading && !canAccessPage(pageKey));

  useInboxMessageAlerts({ workspaceId: activeWorkspaceId ?? null });

  if (blockedByRbac) {
    return <Navigate to="/" replace />;
  }

  return (
    <DateRangeProvider key={activeWorkspaceId ?? "none"}>
      <Outlet />
    </DateRangeProvider>
  );
}
